// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Utils
import { getEMRErrorMessage } from "src/utils/emr/emrErrorHelper";

// Types and API
import { retryEMRExport } from "../../api";

export const useRetryEMRExport = (
  clinicId: string | undefined,
  connectionId: string | undefined
) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (exportId: string) => {
      if (!clinicId) throw new Error();

      const token = await getToken();
      return retryEMRExport(token, clinicId, exportId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["emr-exports", clinicId, connectionId],
      });
      queryClient.invalidateQueries({
        queryKey: ["emr-export-counts", clinicId, connectionId],
      });
      setSnackbarInfo({
        variant: "success",
        message: t("Report queued to send again."),
      });
    },
    onError: (error) => {
      setSnackbarInfo({
        variant: "error",
        message: getEMRErrorMessage(
          error,
          t("Report could not be retried!"),
          t
        ),
      });
    },
  });
};
